import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    TimelineItemDynamicStyleType
} from "./type";
import type {
    Mutable
} from "../../../../types";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    webStyle
} from "../../../../utils";

export const getTimelineSurfaceRadius = ({
    radiuses
}: Pick<TimelineItemDynamicStyleType, "radiuses">) => Math.min(radiuses.actions, radiuses.surface);

const stylesheet = StyleSheet.create({
    container: {
        position: "relative",
        minWidth: 0,
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    wrapper: {
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    row: {
        alignItems: "flex-start",
        flexDirection: "row",
        minWidth: 0
    },
    content: {
        alignItems: "flex-start",
        minWidth: 0,
        flex: 1
    },
    head: {
        alignItems: "baseline",
        alignSelf: "stretch",
        flexDirection: "row",
        flexWrap: "wrap",
        minWidth: 0
    },
    rail: {
        alignItems: "center",
        flexShrink: 0
    },
    attachments: {
        alignSelf: "stretch",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    line: {
        position: "absolute"
    },
    timeEnd: {
        marginStart: "auto"
    },
    description: {
        fontVariant: [
            "tabular-nums"
        ]
    },
    title: {
        flexShrink: 1,
        minWidth: 0
    },
    time: {
        flexShrink: 0
    }
});

export const useStyles = ({
    controlPaddings,
    isFocusVisible,
    shapeConfigs,
    touchTargets,
    pointerType,
    isSurfaced,
    isDashed,
    measures,
    radiuses,
    borders,
    colors,
    isLast,
    spaces
}: TimelineItemDynamicStyleType) => {
    const minHeight = pointerType === "coarse" ? touchTargets.preferred : undefined;

    const styles = {
        content: {
            paddingTop: (measures.markerSize - measures.lineHeight) / 2,
            marginStart: controlPaddings.small,
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        line: {
            start: (measures.markerSize - borders.separator) / 2,
            backgroundColor: colors.content.border.subtle,
            top: measures.markerSize + spaces.spacingXs,
            bottom: isDashed ? 0 : spaces.spacingXs,
            width: borders.separator
        } as Mutable<ViewStyle>,
        container: {
            paddingBottom: isLast ? 0 : measures.gap
        } as Mutable<ViewStyle>,
        surface: {
            ...getCornerStyle({
                radius: getTimelineSurfaceRadius({
                    radiuses
                }),
                shapeConfigs
            }),
            paddingHorizontal: spaces.spacingSm,
            paddingVertical: spaces.spacingXs
        } as Mutable<ViewStyle>,
        wrapper: {
            marginHorizontal: -spaces.spacingSm,
            marginVertical: -spaces.spacingXs,
            minHeight
        } as Mutable<ViewStyle>,
        head: {
            columnGap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        rail: {
            width: measures.markerSize
        } as Mutable<ViewStyle>
    };

    if(isDashed) {
        Object.assign(styles.line, webStyle({
            backgroundImage: `repeating-linear-gradient(to bottom, ${colors.content.border.subtle} 0 ${borders.indicator}px, transparent ${borders.indicator}px ${borders.indicator * 2}px)`
        }));
    }

    if(isSurfaced) {
        Object.assign(styles.surface, getFocusRingStyle({
            colorKey: "default",
            isFocusVisible,
            borders,
            colors
        }));
    }

    return styles;
};
export default stylesheet;
